import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputAdornment,
  Radio,
  RadioGroup,
  ToggleButtonGroup,
  ToggleButton,
  SelectChangeEvent,
  TextField,
  Typography,
  Checkbox,
  Divider,
  Paper,
  Dialog,
} from '@mui/material';
import { API_URL } from '../../config';
// Icon imports for category cards
import SchoolIcon from '@mui/icons-material/School';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ScienceIcon from '@mui/icons-material/Science';
import BuildIcon from '@mui/icons-material/Build';

interface FormData {
  category: string;
  amount: string | number;
  customAmount: string;
  donationType: 'individual' | 'corporate';
  fullName: string;
  email: string;
  phone: string;
  companyName: string;
  taxId: string;
  wantsPrintedReceipt: boolean;
  address: string;

  cardNumber: string;
  expiryDate: string;
  cvv: string;
}

interface DonationFormProps {}

const DonationForm: React.FC<DonationFormProps> = () => {
  const navigate = useNavigate();
  interface DonationCategory {
    value: string;
    label: string;
    icon: React.ReactElement;
  }

  const donationCategories: DonationCategory[] = [
    { value: 'genel', label: 'Genel Bağış', icon: <FavoriteIcon sx={{ fontSize: 30 }} /> },
    { value: 'burs', label: 'Burs Bağışı', icon: <SchoolIcon sx={{ fontSize: 30 }} /> },
    { value: 'akademik', label: 'Akademik Destek', icon: <MenuBookIcon sx={{ fontSize: 30 }} /> },
    { value: 'arastirma', label: 'Araştırma Desteği', icon: <ScienceIcon sx={{ fontSize: 30 }} /> },
    { value: 'altyapi', label: 'Altyapı Desteği', icon: <BuildIcon sx={{ fontSize: 30 }} /> },
  ];

  const donationAmounts = [
    { value: '100', label: '100 TL' },
    { value: '500', label: '500 TL' },
    { value: '1000', label: '1.000 TL' },
    { value: '5000', label: '5.000 TL' },
    { value: '10000', label: '10.000 TL' },
    { value: 'custom', label: 'Özel Tutar' },
  ];

  const initialFormData: FormData = {
    category: 'genel',
    amount: '100',
    customAmount: '',
    donationType: 'individual',
    fullName: '',
    email: '',
    phone: '',
    companyName: '',
    taxId: '',
    wantsPrintedReceipt: false,
    address: '',

    cardNumber: '',
    expiryDate: '',
    cvv: '',
  };

  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [paytenHtml, setPaytenHtml] = useState<string | null>(null);
  const iframeRef = React.useRef<HTMLIFrameElement>(null);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});

  // Listen for postMessage from the Payten iframe containing payment result
  React.useEffect(() => {
    if (!paytenHtml) return;

    const handler = (event: MessageEvent) => {
      if (event.data && typeof event.data === 'object' && 'status' in event.data) {
        if (event.data.status === 'success') {
          navigate('/donation/success');
        } else if (event.data.status === 'error') {
          navigate('/donation/error');
        }
        setPaytenHtml(null);
      }
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [paytenHtml, navigate]);
  

  // Handle form field changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Special handling for expiry date
    if (name === 'expiryDate') {
      // Remove all non-digit characters
      let newValue = value.replace(/\D/g, '');
      
      // Add slash after 2 digits if not already present
      if (newValue.length > 2) {
        newValue = newValue.substring(0, 2) + '/' + newValue.substring(2, 4);
      }
      
      // Limit to 5 characters (MM/YY)
      if (newValue.length > 5) {
        newValue = newValue.substring(0, 5);
      }
      
      setFormData({ ...formData, [name]: newValue });
    } 
    // Special handling for CVV - only allow numbers and limit to 3 characters
    else if (name === 'cvv') {
      const newValue = value.replace(/\D/g, '').substring(0, 3);
      setFormData({ ...formData, [name]: newValue });
    } 
    else {
      setFormData({ ...formData, [name]: value });
    }
    
    // Clear error when user starts typing
    if (errors[name as keyof FormData]) {
      setErrors({ ...errors, [name]: undefined });
    }
  };

  // Handle select changes
  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    // Clear error when user makes a selection
    if (errors[name as keyof FormData]) {
      setErrors({ ...errors, [name]: undefined });
    }
  };

  // Handle donation amount selection
  const handleAmountChange = (amount: string) => {
    setFormData({ ...formData, amount });
    // Clear error when user makes a selection
    if (errors.amount) {
      setErrors({ ...errors, amount: undefined });
    }
  };

  // Handle checkbox changes
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData({ ...formData, [name]: checked });
    // Clear error when user changes checkbox
    if (errors[name as keyof FormData]) {
      setErrors({ ...errors, [name]: undefined });
    }
  };

  const validate = () => {
    let tempErrors: Partial<Record<keyof FormData, string>> = {};
    let isValid = true;

    // Category validation
    if (!formData.category) {
      tempErrors.category = 'Bağış kategorisi seçimi zorunludur.';
      isValid = false;
    }

    // Amount validation
    if (!formData.amount) {
      tempErrors.amount = 'Bağış tutarı seçimi zorunludur.';
      isValid = false;
    } else if (formData.amount === 'custom') {
      if (!formData.customAmount || parseInt(formData.customAmount) < 100) {
        tempErrors.customAmount = 'Minimum bağış tutarı 100 TL\'dir.';
        isValid = false;
      }
    }

    // Donation Type validation
    if (!formData.donationType) {
      tempErrors.donationType = 'Bağış tipi seçimi zorunludur.';
      isValid = false;
    }

    // Name validation (common)
    if (!formData.fullName) {
      tempErrors.fullName = 'Ad Soyad alanı zorunludur.';
      isValid = false;
    }

    // Corporate Information validation
    if (formData.donationType === 'corporate') {
      if (!formData.companyName) {
        tempErrors.companyName = 'Firma Unvanı alanı zorunludur.';
        isValid = false;
      }
      if (!formData.taxId) {
        tempErrors.taxId = 'Vergi Numarası alanı zorunludur.';
        isValid = false;
      }
    }

    // Email validation (for both individual and corporate)
    if (!formData.email) {
      tempErrors.email = 'E-posta alanı zorunludur.';
      isValid = false;
    } else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formData.email)) {
      tempErrors.email = 'Geçerli bir e-posta adresi giriniz.';
      isValid = false;
    }

    // Address Information validation
    if (formData.wantsPrintedReceipt) {
      if (!formData.address) {
        tempErrors.address = 'Adres alanı zorunludur.';
        isValid = false;
      }

    }

    // Credit Card Information validation
    if (!formData.cardNumber) {
      tempErrors.cardNumber = 'Kart Numarası alanı zorunludur.';
      isValid = false;
    } else if (!/^\d{13,19}$/.test(formData.cardNumber)) { // 13-19 digits check
      tempErrors.cardNumber = 'Kart numarası 13-19 haneli olmalıdır.';
      isValid = false;
    }
    if (!formData.expiryDate) {
      tempErrors.expiryDate = 'Son Kullanma Tarihi alanı zorunludur.';
      isValid = false;
    } else {
      const [month, year] = formData.expiryDate.split('/').map(Number);
      const currentYear = new Date().getFullYear() % 100; // Last two digits of current year
      const currentMonth = new Date().getMonth() + 1; // 1-indexed month

      if (year < currentYear || (year === currentYear && month < currentMonth)) {
        tempErrors.expiryDate = 'Kartınızın süresi dolmuştur.';
        isValid = false;
      }
    }
    if (!formData.cvv) {
      tempErrors.cvv = 'CVV alanı zorunludur.';
      isValid = false;
    } else if (!/^\d{3,4}$/.test(formData.cvv)) { // 3 or 4 digits
      tempErrors.cvv = 'Geçerli bir 3 veya 4 haneli CVV giriniz.';
      isValid = false;
    }

    setErrors(tempErrors);
    return isValid;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      const [firstName, ...lastParts] = formData.fullName.trim().split(' ');
        const lastName = lastParts.join(' ');

        const submissionData = {
          category: formData.category,
          amount: formData.amount === 'custom' ? parseInt(formData.customAmount) : parseInt(formData.amount as string),
          customAmount: formData.amount === 'custom' ? formData.customAmount : undefined,
          donationType: formData.donationType,
          firstName,
          lastName,
          email: formData.email,
          phone: formData.phone,
          companyName: formData.companyName,
          taxId: formData.taxId,
          wantsPrintedReceipt: formData.wantsPrintedReceipt,
          address: formData.address,
          cardNumber: formData.cardNumber,
          expiryDate: formData.expiryDate,
          cvv: formData.cvv
        };
      try {
        const apiBase = API_URL;
        const response = await fetch(`${apiBase}/api/donations/initiate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(submissionData),
        });
        
        if (response.ok) {
          const contentType = response.headers.get('content-type') ?? '';
          if (contentType.includes('text/html')) {
            // Backend returned the auto-submit HTML form for Payten
            const html = await response.text();
            setPaytenHtml(html);
            return;
          }
          navigate('/donation/success');
        } else {
          navigate('/donation/error');
        }
      } catch (error) {
        navigate('/donation/error');
      }
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 2 }}>
      <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, border: '2px solid #ccc', borderRadius: '5px' }}>

        <Box component="form" onSubmit={handleSubmit}>
          <Box sx={{ mb: 3 }}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                Bağış Tipi
              </Typography>
              <FormControl component="fieldset" fullWidth error={!!errors.donationType}>
                <RadioGroup
                  row
                  name="donationType"
                  value={formData.donationType}
                  onChange={handleChange}
                  sx={{ justifyContent: 'left' }}
                >
                  <FormControlLabel
                    value="individual"
                    control={<Radio sx={{ color: 'primary.main' }} />}
                    label="Bireysel"
                  />
                  <FormControlLabel
                    value="corporate"
                    control={<Radio />} 
                    label="Kurumsal"
                  />
                </RadioGroup>
                {errors.donationType && <Typography color="error" variant="caption">{errors.donationType}</Typography>}
              </FormControl>
            </Box>

            {/* Donor Information (Common) */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" align="left" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                Bağışçı Bilgileri
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
                <TextField
                  fullWidth
                  label="Ad Soyad"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  required
                  error={!!errors.fullName}
                  helperText={errors.fullName}
                  sx={{ borderRadius: '10px' }}
                />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                <TextField
                  fullWidth
                  label="E-posta"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  error={!!errors.email}
                  helperText={errors.email}
                  sx={{ borderRadius: '10px' }}
                />
                <TextField
                  fullWidth
                  label="Telefon"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  error={!!errors.phone}
                  helperText={errors.phone}
                  sx={{ borderRadius: '10px' }}
                />
              </Box>
            </Box>

            {/* Corporate Information - Only shown if donationType is corporate */}
            {formData.donationType === 'corporate' && (
              <Box sx={{ mb: 3 }}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" align="left" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                  Kurumsal Bilgiler
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
                  <TextField
                    fullWidth
                    label="Firma Unvanı"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    required={formData.donationType === 'corporate'}
                    error={!!errors.companyName}
                    helperText={errors.companyName}
                    sx={{ borderRadius: '10px' }}
                  />
                  <TextField
                    fullWidth
                    label="Vergi Numarası"
                    name="taxId"
                    value={formData.taxId}
                    onChange={handleChange}
                    required={formData.donationType === 'corporate'}
                    error={!!errors.taxId}
                    helperText={errors.taxId}
                    sx={{ borderRadius: '10px' }}
                  />
                </Box>
              </Box>
            )}

            {/* Receipt Information */}
            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.wantsPrintedReceipt}
                    onChange={handleCheckboxChange}
                    name="wantsPrintedReceipt"
                    sx={{ color: 'primary.main' }}
                  />
                }
                label="Basılı makbuz istiyorum"
              />
            </Box>

            {/* Address Information - Only shown if wantsPrintedReceipt is true */}
            {formData.wantsPrintedReceipt && (
              <Box sx={{ mb: 3 }}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" align="left" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                  Adres Bilgileri
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Adres"
                    name="address"
                    multiline
                    rows={3}
                    value={formData.address}
                    onChange={handleChange}
                    required={formData.wantsPrintedReceipt}
                    error={!!errors.address}
                    helperText={errors.address}
                    sx={{ borderRadius: '10px' }}
                  />
                </Box>
              </Box>
            )}
            
              <Box sx={{ gap: 2 }}>
                <FormControl fullWidth margin="dense" variant="outlined" error={!!errors.category}>
                  <FormLabel sx={{ mb: 0.5, fontWeight: 'bold' }}>Bağış Kategorisi</FormLabel>
                  <ToggleButtonGroup
                    color="success"
                    value={formData.category}
                    exclusive
                    onChange={(_e, value) => value && handleSelectChange({ target: { name: 'category', value } } as any)}
                    sx={{ flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}
                  >
                    {donationCategories.map((cat) => (
                      <ToggleButton
                        key={cat.value}
                        value={cat.value}
                        sx={{
                          textTransform: 'none',
                          width: 140,
                          height: 100,
                          display: 'flex',
                          boxShadow: '2px 2px 2px rgba(0, 0, 0, 0.2)',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 1,
                          borderRadius: '12px',
                        }}
                      >
                        {cat.icon}
                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                          {cat.label}
                        </Typography>
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                  {errors.category && <Typography color="error" variant="caption">{errors.category}</Typography>}
                </FormControl>
                <FormControl fullWidth margin="normal" variant="outlined" error={!!errors.amount}>
                  <FormLabel sx={{ mb: 1, fontWeight: 'bold' }}>Bağış Tutarı</FormLabel>
                  <ToggleButtonGroup
                    color="primary"
                    value={formData.amount}
                    exclusive
                    onChange={(_e, value) => value && handleAmountChange(value)}
                    sx={{ flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}
                  >
                    {donationAmounts.map((option) => (
                      <ToggleButton key={option.value} value={option.value} sx={{ textTransform: 'none', px: 2 }}>
                        {option.label}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                  {errors.amount && <Typography color="error" variant="caption">{errors.amount}</Typography>}
                </FormControl>
              </Box>
              {formData.amount === 'custom' && (
                <TextField
                  fullWidth
                  label="Özel Tutar"
                  name="customAmount"
                  value={formData.customAmount}
                  onChange={handleChange}
                  type="number"
                  sx={{ mt: 2, borderRadius: '10px' }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">TL</InputAdornment>,
                  }}
                  error={!!errors.customAmount}
                  helperText={errors.customAmount || "Minimum bağış tutarı 100 TL'dir."}
                />
              )}
            </Box>

            <Divider sx={{ my: 2 }} />
            {/* Credit Card Information */}
            <Box sx={{ mb: 3 }}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" align="left" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                Kredi Kartı Bilgileri
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Kart Numarası"
                  name="cardNumber"
                  value={formData.cardNumber}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const digits = e.target.value.replace(/\D/g, '');
                    if (digits.length <= 19) {
                      handleChange({
                      ...e,
                      target: {
                        ...e.target,
                        value: digits,
                        name: 'cardNumber'
                      }
                    });
                    }
                  }}
                  required
                  inputProps={{ maxLength: 19, inputMode: 'numeric', pattern: '[0-9]*' }}
                  error={!!errors.cardNumber}
                  helperText={errors.cardNumber}
                  sx={{ borderRadius: '10px' }}
                />
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Son Kullanma Tarihi (MM/YY)"
                    name="expiryDate"
                    value={formData.expiryDate}
                    onChange={handleChange}
                    required
                    placeholder="MM/YY"
                    error={!!errors.expiryDate}
                    helperText={errors.expiryDate}
                    sx={{ borderRadius: '10px' }}
                  />
                  <TextField
                    fullWidth
                    label="CVV"
                    name="cvv"
                    value={formData.cvv}
                    onChange={handleChange}
                    required
                    type="number"
                    error={!!errors.cvv}
                    helperText={errors.cvv}
                    sx={{ borderRadius: '10px' }}
                  />
                </Box>
              </Box>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 4 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                sx={{ borderRadius: '4px', py: 1, fontWeight: 'bold' }}
              >
                Bağış Yap
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Payten modal */}
        <Dialog
          open={Boolean(paytenHtml)}
          onClose={(event, reason) => {
            if (reason === 'backdropClick') return;
            setPaytenHtml(null);
          }}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              maxWidth: '100%',
              width: '100%',
              height: 'auto',
              maxHeight: '90vh',
              m: 2,
              backgroundColor: 'white',
              '& .MuiDialog-container': {
                alignItems: 'flex-start',
                pt: 8
              }
            }
          }}
        >
          {paytenHtml && (
            <iframe
              ref={iframeRef}
              title="Payten 3D"
              srcDoc={paytenHtml}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                backgroundColor: 'white',
                minHeight: '500px'
              }}
              onLoad={() => {
                try {
                  const doc = iframeRef.current?.contentDocument;
                  if (!doc) return;
                  const text = doc.body?.innerText || '';
                  if (text.trim().startsWith('{')) {
                    const data = JSON.parse(text);
                    if (data.status === 'success') {
                      navigate('/donation/success');
                    } else if (data.status === 'error') {
                      navigate('/donation/error');
                    }
                    setPaytenHtml(null);
                  }
                } catch (e) {
                  // ignore cross-origin access errors
                }
              }}
            />
          )}
        </Dialog>
      </Container>
  );
};

export default DonationForm;
